import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LearnerFormEditor,
  toast,
} from "@eduviaos/ui-kit";
import type { LearnerFormUpdateRequest, LearnerFormVertical } from "@eduviaos/shared";
import { useTenantLearnerForm, useUpdateTenantLearnerForm } from "@/lib/learnerForm/api";

const PERSON_NOUN: Record<LearnerFormVertical, string> = {
  study_centre: "learner",
  school: "student",
  coaching_institute: "student",
};

/**
 * Phase SC-22 — the super admin's editor for a tenant's learner form.
 * Shows every standard field (hidden ones included) so the SA can turn
 * a vertical-hidden field back on for one tenant.
 */
export function LearnerFormPanel({ tenantSlug }: { tenantSlug: string }) {
  const formQuery = useTenantLearnerForm(tenantSlug);
  const save = useUpdateTenantLearnerForm(tenantSlug);
  const form = formQuery.data;
  const noun = form ? PERSON_NOUN[form.vertical] : "learner";
  const title = `${noun.charAt(0).toUpperCase()}${noun.slice(1)} form`;

  async function handleSave(body: LearnerFormUpdateRequest) {
    try {
      await save.mutateAsync(body);
      toast.success(`${title} saved.`);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : "Couldn't save the form.");
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{title}</CardTitle>
          {form?.is_customised ? <Badge variant="secondary">Customised</Badge> : null}
        </div>
        <CardDescription>
          The fields shown when this tenant adds or edits a {noun}. Rename, reorder, hide or require
          standard fields, and add extra fields. Values for extra fields are kept on the {noun}
          &apos;s record.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {formQuery.isLoading ? (
          <div className="grid gap-2" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="bg-muted h-10 animate-pulse rounded-md motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : formQuery.isError || !form ? (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <p className="text-destructive" role="alert">
              Couldn&apos;t load the {title.toLowerCase()}.
            </p>
            <Button variant="outline" size="sm" onClick={() => void formQuery.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <LearnerFormEditor
            form={form}
            saving={save.isPending}
            onSave={(body) => void handleSave(body)}
            personNoun={noun}
          />
        )}
      </CardContent>
    </Card>
  );
}
